// src/pages/FamilyDirectoryPage.js
// Updated: 10/18/2025 11:59 PM

import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/common/layout/Layout';
import { useFamily } from '../context/FamilyContext';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import Spinner from '../components/common/Spinner';
import { FaUserCircle, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import toast from 'react-hot-toast'; // *** ADDED THIS IMPORT ***

const FamilyDirectoryPage = () => {
  const { familyData } = useFamily();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!familyData) {
        setLoading(false); // Set loading false if no family data
        return;
    }
    setLoading(true);
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('familyId', '==', familyData.id));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const memberList = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        // --- UPDATED SORTING LOGIC ---
        .sort((a, b) => {
            const lastNameA = a.lastName || '';
            const lastNameB = b.lastName || '';
            const firstNameA = a.firstName || '';
            const firstNameB = b.firstName || '';

            if (lastNameA.toLowerCase() < lastNameB.toLowerCase()) return -1;
            if (lastNameA.toLowerCase() > lastNameB.toLowerCase()) return 1;
            // If last names are the same, sort by first name
            if (firstNameA.toLowerCase() < firstNameB.toLowerCase()) return -1;
            if (firstNameA.toLowerCase() > firstNameB.toLowerCase()) return 1;
            return 0; // Names are identical
        });
        // --- END UPDATED SORTING LOGIC ---
      setMembers(memberList);
      setLoading(false);
    }, (error) => { // Added error handling
        console.error("Error fetching family members:", error);
        toast.error("Could not load family directory."); // toast is now defined
        setLoading(false);
    });

    return () => unsubscribe();
  }, [familyData]);

  // Filtered members logic remains the same
  const filteredMembers = useMemo(() => {
    if (!searchTerm) return members;
    return members.filter(member =>
      `${member.firstName || ''} ${member.lastName || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [members, searchTerm]);


  // JSX Return remains the same structure
  return (
    <Layout>
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h1 className="text-3xl font-bold text-neutral-gray">Family Directory</h1>
        <p className="mt-2 text-gray-600">Search for contact information for members of the {familyData?.familyName || 'family'}.</p>
        <div className="mt-4">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-sm border-gray-300 rounded-md shadow-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Spinner size="lg" /></div>
      ) : !familyData ? ( // Added check for no familyData
        <p className="text-gray-500 text-center">Please join or create a family first.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMembers.length > 0 ? filteredMembers.map(member => (
            <div key={member.id} className="bg-white p-4 rounded-lg shadow-md flex items-start gap-4">
              {member.profilePictureUrl ? (
                <img src={member.profilePictureUrl} alt={`${member.firstName || ''} ${member.lastName || ''}`} className="h-16 w-16 rounded-full object-cover flex-shrink-0" />
              ) : (
                <FaUserCircle className="h-16 w-16 text-gray-300 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-lg text-primary truncate">{member.firstName || ''} {member.lastName || ''}</p>
                {member.isDeceased ? (
                    <p className="text-sm text-gray-500 italic">Deceased</p>
                ) : (
                    <div className="space-y-1 mt-2 text-sm text-gray-600">
                        {member.email && <p className="flex items-center gap-2 truncate"><FaEnvelope size={12}/> {member.email}</p>}
                        {member.phone && <p className="flex items-center gap-2 truncate"><FaPhone size={12}/> {member.phone}</p>}
                        {member.address && <p className="flex items-start gap-2"><FaMapMarkerAlt size={12} className="mt-1 flex-shrink-0"/> <span className="truncate">{member.address}</span></p>}
                    </div>
                )}
              </div>
            </div>
          )) : (
             <p className="text-gray-500 md:col-span-2 lg:col-span-3 text-center">No members found matching your search.</p>
          )}
        </div>
      )}
    </Layout>
  );
};

export default FamilyDirectoryPage;